// ** UI
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme, useMediaQuery } from '@mui/material';

// ** Components
import StrapiImageRenderer from '@/components/strapi/StrapiImageRenderer';
import BlocksContentRenderer from '@/components/strapi/BlocksContentRenderer';

// ** Types
import type { AccordionSubComponentProps } from './index';

const ACCORDION_IMAGE_WIDTH = '374.45px';
// const ACCORDION_IMAGE_HEIGHT = '250.167px';
const ACCORDION_IMAGE_ASPECT_RATIO = '232 / 155';

const TextBlockComponent = ({ host, accordionElementTypeTextBlock }: AccordionSubComponentProps) => {
	const theme = useTheme();
	// Check if the screen is 'sm' or larger (600px+)
	const isUpSm = useMediaQuery(theme.breakpoints.up('sm'));

	const {
		accordionElementTextBlockImage,
		accordionElementTextBlockContent,
		accordionElementTextBlockCopyAccordion,
		accordionElementTextBlockTextEditorType
	} = accordionElementTypeTextBlock || {};
	const contentArray =
		(typeof accordionElementTextBlockContent?.blockContent === 'string' &&
			JSON.parse(accordionElementTextBlockContent.blockContent)) ||
		[];

	return (
		<Stack spacing={3}>
			{accordionElementTextBlockTextEditorType === 'Standard' ? (
				<Typography
					variant="body-md"
					sx={{
						width: '100%',
						maxWidth: { xs: '800px', md: '1040px' },
						color: theme.palette.text.secondary ?? theme.palette.text.primary
					}}
				>
					{accordionElementTextBlockCopyAccordion}
				</Typography>
			) : accordionElementTextBlockTextEditorType === 'Block' ? (
				<BlocksContentRenderer
					content={contentArray}
					options={{
						variantBody: accordionElementTextBlockContent?.variantBody,
						variantHeading1: accordionElementTextBlockContent?.variantHeading1,
						variantHeading2: accordionElementTextBlockContent?.variantHeading2,
						variantHeading3: accordionElementTextBlockContent?.variantHeading3,
						variantHeading4: accordionElementTextBlockContent?.variantHeading4,
						variantHeading5: accordionElementTextBlockContent?.variantHeading5,
						variantHeading6: accordionElementTextBlockContent?.variantHeading6,
						overrides: {
							paragraph: {
								mt: 0,
								fontFamily: '"DIN Next LT Pro"',
								fontSize: 'clamp(1rem, 0.9554rem + 0.2232vw, 1.125rem)',
								fontWeight: 400,
								lineHeight: 'clamp(1.375rem, 1.2857rem + 0.4464vw, 1.625rem)',
								letterSpacing: '-0.05px',
								[theme.breakpoints.up('md')]: {
									fontSize: 'clamp(1rem, 0.9554rem + 0.2232vw, 1.125rem)'
								}
							},
							heading: {
								fontFamily: '"DIN Next LT Pro"',
								mb: 1
							},
							list: {
								pl: 4,
								'& li': {
									p: 0.3,
									fontFamily: '"DIN Next LT Pro"',
									fontSize: 'clamp(1rem, 0.9554rem + 0.2232vw, 1.125rem)'
								}
							},
							listItem: {
								fontFamily: '"DIN Next LT Pro"',
								fontSize: 'clamp(1rem, 0.9554rem + 0.2232vw, 1.125rem)',
								'& .MuiTypography-root': {
									fontSize: 'inherit'
								},
								[theme.breakpoints.up('md')]: {
									fontSize: 'clamp(1rem, 0.9554rem + 0.2232vw, 1.125rem)'
								}
							},
							link: {
								textDecoration: 'underline',
								fontFamily: '"DIN Next LT Pro"',
								'&:hover': {
									opacity: 0.8
								},
								[theme.breakpoints.up('md')]: {
									fontSize: 'clamp(1rem, 0.9554rem + 0.2232vw, 1.125rem)'
								}
							},
							quote: {
								borderColor: theme.palette.primary.light,
								backgroundColor: 'rgba(255, 255, 255, 0.1)',
								[theme.breakpoints.up('md')]: {
									fontSize: 'clamp(1rem, 0.9554rem + 0.2232vw, 1.125rem)'
								}
							}
						}
					}}
				/>
			) : null}

			{accordionElementTextBlockImage && (
				<StrapiImageRenderer
					host={host}
					imageData={accordionElementTextBlockImage}
					alt={accordionElementTextBlockImage.alternativeText || 'Accordion Content'}
					height={250}
					width={375}
					sizes="(max-width:600px) 100vw, 375px"
					style={{
						// Responsive
						width: isUpSm ? ACCORDION_IMAGE_WIDTH : '100%',
						maxWidth: ACCORDION_IMAGE_WIDTH,
						height: 'auto',
						aspectRatio: ACCORDION_IMAGE_ASPECT_RATIO,

						// Static styles
						objectFit: 'cover',
						display: 'block',
						borderRadius: '16px',

						// Spacing
						marginTop: accordionElementTextBlockCopyAccordion ? '1.5rem' : '24px'
					}}
				/>
			)}
		</Stack>
	);
};

export default TextBlockComponent;
